import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { css, useTheme, jsx, ClassNames } from '@emotion/react';
import { math } from 'polished';
import { devWarning, utilStyles, ZINDEX_DROPDOWN, KeyboardCode } from '@paypalcorp/pp-react-utils';
import { textStyles, CaptionText, BodyText } from '@paypalcorp/pp-react-text';
import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import React, { useMemo, useState, useRef, useLayoutEffect, forwardRef, useContext, useCallback, useEffect } from 'react';
import { CalendarIcon, ClockSmIcon, ArrowRightIcon, ArrowLeftIcon } from '@paypalcorp/pp-react-icons';
import { MaskType, MaskedTextInput } from '@paypalcorp/pp-react-masked-text-input';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { Sheet } from '@paypalcorp/pp-react-modal';
import { IntlContext } from '@paypalcorp/pp-react-intl';
import { IconButton } from '@paypalcorp/pp-react-icon-button';
var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12, _templateObject13, _templateObject14, _templateObject15, _templateObject16, _templateObject17, _templateObject18, _templateObject19, _templateObject20, _templateObject21, _templateObject22, _templateObject23, _templateObject24, _templateObject25, _templateObject26, _templateObject27, _templateObject28, _templateObject29, _templateObject30, _templateObject31, _templateObject32;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }
  var sysColorBackgroundSurface = tokens.sysColorBackgroundSurface,
    sysSpaceLg = tokens.sysSpaceLg,
    sysBorderRadiusSm = tokens.sysBorderRadiusSm,
    sysBorderThicknessNormal = tokens.sysBorderThicknessNormal,
    sysBorderRadiusInteractive = tokens.sysBorderRadiusInteractive,
    sysColorBackgroundHighContrast = tokens.sysColorBackgroundHighContrast,
    sysColorPrimaryMainHover = tokens.sysColorPrimaryMainHover,
    sysBorderThicknessEmphasis = tokens.sysBorderThicknessEmphasis,
    sysSizeMediaMd = tokens.sysSizeMediaMd,
    sysColorSpecialtyMain = tokens.sysColorSpecialtyMain,
    sysColorBackgroundMediumContrast = tokens.sysColorBackgroundMediumContrast,
    sysSpaceTiny = tokens.sysSpaceTiny,
    sysColorSelectedRangeMain = tokens.sysColorSelectedRangeMain,
    sysColorSelectedMain = tokens.sysColorSelectedMain,
    sysSizeIconLg = tokens.sysSizeIconLg,
    sysSizePointerWidthLg = tokens.sysSizePointerWidthLg,
    sysSizeTapTargetDefault = tokens.sysSizeTapTargetDefault,
    sysSizeTapTargetSm = tokens.sysSizeTapTargetSm,
    sysSizeTapTargetXs = tokens.sysSizeTapTargetXs,
    sysSizeFieldLg = tokens.sysSizeFieldLg,
    sysSpaceSm = tokens.sysSpaceSm,
    sysSpaceXl = tokens.sysSpaceXl,
    sysTypographyCaptionSize = tokens.sysTypographyCaptionSize,
    sysSpaceXs = tokens.sysSpaceXs,
    sysColorSelectedContrast = tokens.sysColorSelectedContrast,
    sysColorSelectedRangeContrast = tokens.sysColorSelectedRangeContrast;
  var _utilStyles = utilStyles(tokens),
    elevationStyles = _utilStyles.elevationStyles,
    caretStyles = _utilStyles.caretStyles;
  var _textStyles = textStyles(tokens),
    textBody = _textStyles.body;
  var calendarContainerOffset = math("".concat(sysSizeFieldLg, " + ").concat(sysSpaceSm)); // offset from calendar icon

  var xsBreakpoint = '22.5rem';
  var smBreakpoint = '25rem';
  function getDateDimensions(dateSize) {
    return css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n      width: ", ";\n      height: ", ";\n    "])), dateSize, dateSize);
  }
  var calendarTrigger = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: calendar_trigger;\n    cursor: pointer;\n  "])));
  function calendarContainerStyle() {
    return css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n      background-color: ", ";\n      position: absolute;\n      top: ", ";\n      padding: ", ";\n      border: ", " solid ", ";\n      border-radius: ", ";\n      ", "\n      ", "\n      ", "\n      ", ";\n      z-index: ", ";\n      cursor: initial;\n    "])), sysColorBackgroundSurface, calendarContainerOffset, sysSpaceLg, sysBorderThicknessNormal, sysColorBackgroundSurface, sysBorderRadiusSm, elevationStyles.level2, caretStyles.base(sysSizePointerWidthLg), caretStyles.positionTop(math("".concat(sysSizePointerWidthLg, " / 2"))), caretStyles.positionCenter(sysSizePointerWidthLg), ZINDEX_DROPDOWN);
  }
  var calendarContainer = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: calendar_container;\n    ", "\n    &:after {\n      top: -", ";\n    }\n  "])), calendarContainerStyle(), sysBorderThicknessNormal);
  var calendarContainerLeft = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: calendar_container-left;\n    ", "\n    right: -", ";\n    &:before {\n      left: calc(100% - ((", " / 2) + ", "));\n    }\n    &:after {\n      top: -", ";\n      left: calc(100% - ((", " / 2) + ", "));\n    }\n  "])), calendarContainerStyle(), sysSpaceSm, sysSizeIconLg, sysSpaceLg, sysBorderThicknessNormal, sysSizeIconLg, sysSpaceXl);
  var calendarContainerRight = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: calendar_container-right;\n    ", "\n    left: -", ";\n    &:before {\n      left: calc(", " / 2);\n    }\n    &:after {\n      top: -", ";\n      left: calc((", " / 2) - ", ");\n    }\n  "])), calendarContainerStyle(), sysSpaceSm, sysSizeIconLg, sysBorderThicknessNormal, sysSizeIconLg, sysSpaceSm);
  var calendarNav = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: calendar_nav;\n    display: flex;\n    align-items: center;\n    justify-content: space-between;\n    white-space: nowrap;\n  "])));
  var calendarNavButtonPrev = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: nav_prev_btn;\n  "])));
  var calendarNavButtonNext = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: nav_next_btn;\n  "])));
  var calendarNavButtonsNotFocusable = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    &:after {\n      display: none;\n    }\n  "])));
  var calendarNavButtonsHidden = css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    label: hidden;\n    visibility: hidden;\n  "])));
  var fullWidth = css(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n    width: 100%;\n  "])));
  var tableFullWidth = css(_templateObject13 || (_templateObject13 = _taggedTemplateLiteral(["\n    table {\n      ", "\n    }\n  "])), fullWidth);
  var calendarTable = css(_templateObject14 || (_templateObject14 = _taggedTemplateLiteral(["\n    label: calendar_table;\n    border-spacing: 0;\n\n    th,\n    td {\n      text-align: center;\n      padding: ", " 0;\n    }\n\n    @media (max-width: ", ") {\n      ", "\n    }\n  "])), sysSpaceTiny, sysSizeMediaMd, fullWidth);
  var calendarCellInRange = css(_templateObject15 || (_templateObject15 = _taggedTemplateLiteral(["\n    label: cell_in_range;\n    border-top: ", " solid transparent;\n    border-bottom: ", " solid transparent;\n    background-color: ", ";\n  "])), sysBorderThicknessNormal, sysBorderThicknessNormal, sysColorSelectedRangeMain);
  var calendarCellRangeStart = css(_templateObject16 || (_templateObject16 = _taggedTemplateLiteral(["\n    label: range_start;\n    background: linear-gradient(\n      to right,\n      ", " 50%,\n      ", " 50%\n    );\n  "])), sysColorBackgroundSurface, sysColorSelectedRangeMain);
  var calendarCellRangeEnd = css(_templateObject17 || (_templateObject17 = _taggedTemplateLiteral(["\n    label: range_end;\n    background: linear-gradient(\n      to right,\n      ", " 50%,\n      ", " 50%\n    );\n  "])), sysColorSelectedRangeMain, sysColorBackgroundSurface);
  var calendarDate = css(_templateObject18 || (_templateObject18 = _taggedTemplateLiteral(["\n    label: date;\n    ", "\n    border: none;\n    padding: 0;\n    margin: 0 ", ";\n    text-decoration: none;\n    cursor: pointer;\n    ", "\n    border-radius: ", ";\n    background-color: transparent;\n\n    &:hover {\n      border: ", " solid ", ";\n    }\n\n    @media (max-width: ", ") {\n      ", "\n    }\n\n    @media (min-width: ", ") and (max-width: ", ") {\n      ", "\n    }\n  "])), textBody(), sysSpaceTiny, getDateDimensions(sysSizeTapTargetDefault), sysBorderRadiusInteractive, sysBorderThicknessEmphasis, sysColorPrimaryMainHover, xsBreakpoint, getDateDimensions(sysSizeTapTargetXs), xsBreakpoint, smBreakpoint, getDateDimensions(sysSizeTapTargetSm));
  var calendarDateInRange = css(_templateObject19 || (_templateObject19 = _taggedTemplateLiteral(["\n    label: date_in_range;\n    color: ", ";\n  "])), sysColorSelectedRangeContrast);
  var calendarDateNotCurrent = css(_templateObject20 || (_templateObject20 = _taggedTemplateLiteral(["\n    label: date_not_current;\n    color: ", ";\n  "])), sysColorBackgroundMediumContrast);
  var calendarDateDisabled = css(_templateObject21 || (_templateObject21 = _taggedTemplateLiteral(["\n    label: disabled;\n    text-decoration-line: line-through;\n    cursor: not-allowed;\n    color: ", ";\n\n    &:hover {\n      border: none;\n    }\n  "])), sysColorBackgroundMediumContrast);
  var calendarDateToday = css(_templateObject22 || (_templateObject22 = _taggedTemplateLiteral(["\n    label: today;\n    border: ", " solid ", ";\n  "])), sysBorderThicknessNormal, sysColorBackgroundHighContrast);
  var calendarDateSelected = css(_templateObject23 || (_templateObject23 = _taggedTemplateLiteral(["\n    label: selected;\n    color: ", ";\n    border: ", " solid ", ";\n    background-color: ", ";\n  "])), sysColorSelectedContrast, sysBorderThicknessNormal, sysColorSelectedMain, sysColorSelectedMain);
  var calendarDueDate = css(_templateObject24 || (_templateObject24 = _taggedTemplateLiteral(["\n    label: due_date;\n    color: ", ";\n    border: ", " solid ", ";\n  "])), sysColorSpecialtyMain, sysBorderThicknessNormal, sysColorSpecialtyMain);
  var calendarDueDateSelected = css(_templateObject25 || (_templateObject25 = _taggedTemplateLiteral(["\n    label: due_date_selected;\n    border: ", " solid ", ";\n  "])), sysBorderThicknessNormal, sysColorSpecialtyMain);
  var calendarDueDateIconContainer = css(_templateObject26 || (_templateObject26 = _taggedTemplateLiteral(["\n    label: icon_container;\n    display: inline-block;\n    position: relative;\n    ", "\n\n    @media (max-width: ", ") {\n      ", "\n    }\n\n    @media (min-width: ", ") and (max-width: ", ") {\n      ", "\n    }\n  "])), getDateDimensions(sysSizeTapTargetDefault), xsBreakpoint, getDateDimensions(sysSizeTapTargetXs), xsBreakpoint, smBreakpoint, getDateDimensions(sysSizeTapTargetSm));
  var calendarDueDateIcon = css(_templateObject27 || (_templateObject27 = _taggedTemplateLiteral(["\n    label: icon;\n    position: absolute;\n    bottom: 0;\n    right: 0;\n    pointer-events: none;\n    background-color: ", ";\n    border-radius: ", ";\n    color: ", ";\n  "])), sysColorBackgroundSurface, sysBorderRadiusInteractive, sysColorSpecialtyMain);
  var legend = css(_templateObject28 || (_templateObject28 = _taggedTemplateLiteral(["\n    label: legend;\n    display: flex;\n    align-items: center;\n    margin-top: ", ";\n    margin-inline-start: ", ";\n  "])), sysSpaceSm, sysSpaceLg);
  var legendItem = css(_templateObject29 || (_templateObject29 = _taggedTemplateLiteral(["\n    label: legend_item;\n    display: flex;\n    align-items: center;\n    white-space: nowrap;\n    margin-inline-end: ", ";\n  "])), sysSpaceLg);
  var legendKey = css(_templateObject30 || (_templateObject30 = _taggedTemplateLiteral(["\n    label: key;\n    width: ", ";\n    height: ", ";\n    display: inline-block;\n    margin-inline-end: ", ";\n    border-radius: ", ";\n  "])), sysTypographyCaptionSize, sysTypographyCaptionSize, sysSpaceXs, sysBorderRadiusInteractive);
  var dueDateLegendKey = css(_templateObject31 || (_templateObject31 = _taggedTemplateLiteral(["\n    label: due_date_legend_key;\n    background-color: ", ";\n    color: ", ";\n    margin-inline-end: ", ";\n    border-radius: ", ";\n  "])), sysColorBackgroundSurface, sysColorSpecialtyMain, sysSpaceXs, sysBorderRadiusInteractive);
  var wrapperContainer = css(_templateObject32 || (_templateObject32 = _taggedTemplateLiteral(["\n    label: wrapper;\n    [dir='rtl'] & input {\n      text-align: right;\n      direction: ltr;\n    }\n  "])));
  return {
    calendarTrigger: calendarTrigger,
    calendarContainer: calendarContainer,
    calendarContainerLeft: calendarContainerLeft,
    calendarContainerRight: calendarContainerRight,
    calendarNav: calendarNav,
    calendarTable: calendarTable,
    calendarNavButtonPrev: calendarNavButtonPrev,
    calendarNavButtonNext: calendarNavButtonNext,
    calendarNavButtonsNotFocusable: calendarNavButtonsNotFocusable,
    calendarNavButtonsHidden: calendarNavButtonsHidden,
    calendarCellInRange: calendarCellInRange,
    calendarCellRangeStart: calendarCellRangeStart,
    calendarCellRangeEnd: calendarCellRangeEnd,
    calendarDate: calendarDate,
    calendarDateNotCurrent: calendarDateNotCurrent,
    calendarDateDisabled: calendarDateDisabled,
    calendarDateToday: calendarDateToday,
    calendarDateSelected: calendarDateSelected,
    calendarDueDate: calendarDueDate,
    calendarDueDateIconContainer: calendarDueDateIconContainer,
    calendarDueDateIcon: calendarDueDateIcon,
    calendarDateInRange: calendarDateInRange,
    calendarDueDateSelected: calendarDueDateSelected,
    legend: legend,
    legendItem: legendItem,
    legendKey: legendKey,
    dueDateLegendKey: dueDateLegendKey,
    tableFullWidth: tableFullWidth,
    wrapperContainer: wrapperContainer
  };
}
var MD_BREAKPOINT = 752;
var BASE_FONT_SIZE = 16;
var BASE_CALENDAR_IDEAL_WIDTH = 200;
var CalendarContainer = function (_ref) {
  var isCalendarVisible = _ref.isCalendarVisible,
    label = _ref.label,
    children = _ref.children,
    calendarTriggerRef = _ref.calendarTriggerRef,
    wrapperContainerRef = _ref.wrapperContainerRef,
    onShowCalendar = _ref.onShowCalendar,
    _ref$calendarInSheet = _ref.calendarInSheet,
    calendarInSheet = _ref$calendarInSheet === void 0 ? false : _ref$calendarInSheet;
  var tokens = useTheme();
  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isMobile = _useState2[0],
    setMobile = _useState2[1];
  var containerRef = useRef();
  var _useState3 = useState(_styles.calendarContainer),
    _useState4 = _slicedToArray(_useState3, 2),
    calendarPosition = _useState4[0],
    setCalendarPosition = _useState4[1];
  var isMobileViewPort = function () {
    return /* istanbul ignore next */window && window.innerWidth < MD_BREAKPOINT;
  };
  /* istanbul ignore next */

  var handleKey = function (evt) {
    if (new KeyboardCode(evt.key || evt.keyCode).IS_ESC) {
      onShowCalendar(false);
    }
  };
  /* istanbul ignore next */

  var handleOutsideClick = function (evt) {
    if (!calendarTriggerRef.current.contains(evt.target) && !containerRef.current.contains(evt.target)) {
      onShowCalendar(false);
    }
  };
  /* istanbul ignore next */

  var handleResize = function () {
    /* Event to dismiss calendar popup on window resize */
    onShowCalendar(false);
  };
  var getRefCurrent = function (myref) {
    return /* istanbul ignore next */myref && myref.current ? myref.current : null;
  };
  /* istanbul ignore next */

  var getCalendarBoundary = function () {
    var calendarTrigger = getRefCurrent(calendarTriggerRef);
    var calendarTriggerPos = calendarTrigger.getBoundingClientRect();
    var windowWidth = window && window.innerWidth || document.documentElement.clientWidth;
    var fontSizeVal = window && parseFloat(window.getComputedStyle(document.documentElement).fontSize);
    var calculatedSpace = BASE_CALENDAR_IDEAL_WIDTH + BASE_CALENDAR_IDEAL_WIDTH * ((fontSizeVal - BASE_FONT_SIZE) * 0.0625);
    if (wrapperContainerRef === null || wrapperContainerRef === void 0 ? void 0 : wrapperContainerRef.current) {
      var wrapperContainerPos = wrapperContainerRef.current.getBoundingClientRect();
      if (calendarTriggerPos.left - wrapperContainerPos.left < calculatedSpace) {
        // Left side is out of viewoprt
        setCalendarPosition(_styles.calendarContainerRight);
      } else if (wrapperContainerPos.right + calculatedSpace > wrapperContainerPos.width) {
        // Right side is out of viewport
        setCalendarPosition(_styles.calendarContainerLeft);
      }
    } else if (calendarTrigger) {
      if (calendarTriggerPos.left < calculatedSpace) {
        // Left side is out of viewoprt
        setCalendarPosition(_styles.calendarContainerRight);
      } else if (calendarTriggerPos.right + calculatedSpace > windowWidth) {
        // Right side is out of viewport
        setCalendarPosition(_styles.calendarContainerLeft);
      }
    }
  };
  useLayoutEffect(function () {
    setCalendarPosition(_styles.calendarContainer);
    var _isMobile = isMobileViewPort();
    getCalendarBoundary();
    setMobile(_isMobile);
    if (!_isMobile && isCalendarVisible) {
      var _containerRef$current, _containerRef$current2;
      window.addEventListener('resize', handleResize);
      document.addEventListener('keydown', handleKey); // Add handleOutSideClick when calendar is not in sheet, let sheet handle outside click on its own

      /* istanbul ignore next */

      if (!calendarInSheet) {
        document.addEventListener('click', handleOutsideClick);
      } // eslint-disable-next-line no-unused-expressions

      (_containerRef$current = containerRef.current) === null || _containerRef$current === void 0 ? void 0 : (_containerRef$current2 = _containerRef$current.scrollIntoView) === null || _containerRef$current2 === void 0 ? void 0 : _containerRef$current2.call(_containerRef$current, {
        behavior: 'smooth',
        block: 'nearest'
      });
    }
    return function () {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isCalendarVisible]);
  if (isMobile || calendarInSheet) {
    /* istanbul ignore next */
    return jsx(Sheet, {
      isOpen: isCalendarVisible,
      onCloseComplete: function onCloseComplete() {
        return onShowCalendar(false);
      },
      noCloseButton: true,
      title: label,
      skipFormFocus: true,
      "data-ppui": "true"
    }, calendarInSheet && !isMobile ? jsx("div", {
      css: _styles.tableFullWidth,
      "data-ppui": "true"
    }, children) : children);
  }
  return isCalendarVisible ? jsx("div", {
    css: calendarPosition,
    ref: containerRef,
    "data-ppui": "true"
  }, children) : null;
};
var _excluded = ["name", "label", "className", "calendar", "isCalendarVisible", "wrapperContainerRef", "onShowCalendar", "maskOptions", "selectedDate", "onSelection", "parseDate", "formatDate", "formatterSkeleton", "sheetContainerRef", "onComplete", "onAccept", "calendarInSheet"];
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
var DEFAULT_MASK_OPTIONS = {
  mask: 'm`/d`/y',
  // here backquote ` is needed to prevent characters shifting back on edit. Refer https://github.com/uNmAnNeR/imaskjs/issues/89
  lazy: false,
  blocks: {
    d: {
      mask: MaskType.Range,
      from: 1,
      to: 31,
      maxLength: 2,
      placeholderChar: 'D'
    },
    m: {
      mask: MaskType.Range,
      from: 1,
      to: 12,
      maxLength: 2,
      placeholderChar: 'M'
    },
    y: {
      mask: MaskType.Range,
      from: 1900,
      to: 2999,
      maxLength: 4,
      placeholderChar: 'Y'
    }
  }
}; // eslint-disable-next-line

var localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
function defaultParseDate(str) {
  /* istanbul ignore next */
  if (!str) {
    return null;
  }
  var parts = str.split('/');
  var month = Number(parts[0]);
  var date = Number(parts[1]);
  var year = Number(parts[2]);
  return new Date(year, month - 1, date);
}
function defaultFormatDate(date) {
  if (!date) {
    return '';
  }
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  }).format(date);
}
function getDateMaskOptions(intlContext, formatterSkeleton, maskOptions) {
  if (!(intlContext && intlContext.dateTimeFormatter)) {
    return _objectSpread(_objectSpread({}, DEFAULT_MASK_OPTIONS), maskOptions);
  } // Get pattern from Worldready

  var dateTimeFormatter = intlContext.dateTimeFormatter({
    style: formatterSkeleton,
    timeZone: localTimeZone
  });
  var parts = dateTimeFormatter.formatToParts(new Date());
  var pattern = parts.map(function (_ref) {
    var type = _ref.type,
      value = _ref.value;
    switch (type) {
      case 'month':
        return 'm';
      case 'day':
        return 'd';
      case 'year':
        return 'y';
      default:
        // Remove any rtl marker chars
        // eslint-disable-next-line
        var separator = value.replace(/[^\x00-\x7F]/g, '');
        return "`".concat(separator);
    }
  }).join('');
  return _objectSpread(_objectSpread(_objectSpread({}, DEFAULT_MASK_OPTIONS), maskOptions), {}, {
    mask: pattern
  });
}
function getFormatters(intlContext, formatterSkeleton, formatDate, parseDate) {
  if (!(intlContext && intlContext.dateTimeFormatter)) {
    return {
      _formatDate: formatDate || defaultFormatDate,
      _parseDate: parseDate || defaultParseDate
    };
  } // Get formatters from Worldready

  var dateTimeFormatter = intlContext.dateTimeFormatter({
    style: formatterSkeleton,
    timeZone: localTimeZone
  });
  return {
    _formatDate: formatDate || function _formatDate(date) {
      /* istanbul ignore next */
      if (!date) {
        return '';
      }
      return dateTimeFormatter.format(date);
    },
    _parseDate: parseDate || function _parseDate(str) {
      return str && dateTimeFormatter.parse(str);
    }
  };
}
var DateInput = forwardRef(function (_ref2, ref) {
  var name = _ref2.name,
    label = _ref2.label,
    className = _ref2.className,
    calendar = _ref2.calendar,
    isCalendarVisible = _ref2.isCalendarVisible,
    wrapperContainerRef = _ref2.wrapperContainerRef,
    onShowCalendar = _ref2.onShowCalendar,
    maskOptions = _ref2.maskOptions,
    selectedDate = _ref2.selectedDate,
    onSelection = _ref2.onSelection,
    parseDate = _ref2.parseDate,
    formatDate = _ref2.formatDate,
    _ref2$formatterSkelet = _ref2.formatterSkeleton,
    formatterSkeleton = _ref2$formatterSkelet === void 0 ? 'yyyyMMdd' : _ref2$formatterSkelet,
    sheetContainerRef = _ref2.sheetContainerRef,
    onComplete = _ref2.onComplete,
    onAccept = _ref2.onAccept,
    calendarInSheet = _ref2.calendarInSheet,
    rest = _objectWithoutProperties(_ref2, _excluded);
  if (!onSelection) {
    onSelection = function () {
      return devWarning('Required prop `onSelection` is missing');
    };
  }
  var tokens = useTheme();
  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  var intlContext = useContext(IntlContext);
  var calendarTriggerRef = useRef();
  var internalRef = useRef();
  var inputRef = ref || internalRef;
  var prevVal = useRef();
  var _maskOptions = useMemo(function () {
    return getDateMaskOptions(intlContext, formatterSkeleton, maskOptions);
  }, [formatterSkeleton, maskOptions]);
  var _useMemo = useMemo(function () {
      return getFormatters(intlContext, formatterSkeleton, formatDate, parseDate);
    }, [formatDate, parseDate]),
    _formatDate = _useMemo._formatDate,
    _parseDate = _useMemo._parseDate;
  var handleComplete = function (value, unmaskedValue, typedValue) {
    /* istanbul ignore next */
    if (unmaskedValue !== prevVal.current) {
      onSelection(_parseDate(value));
    }
    if (onComplete) {
      onComplete(value, unmaskedValue, typedValue);
    }
  };
  var handleAccept = function (value, unmaskedValue, typedValue) {
    if (!unmaskedValue) {
      onSelection(null);
    }
    if (onAccept) {
      onAccept(value, unmaskedValue, typedValue);
    }
  };
  var icon = calendar && jsx(React.Fragment, null, jsx(CalendarIcon, {
    size: "sm",
    onMouseDown: function onMouseDown(evt) {
      return evt.preventDefault();
    },
    onClick: function handleIconClick() {
      var inputElm = inputRef.current;
      inputElm.focus();
      onShowCalendar(!isCalendarVisible);
    },
    svgElementRef: calendarTriggerRef,
    color: "sysColorBackgroundMediumContrast",
    css: _styles.calendarTrigger,
    "aria-hidden": "true",
    "data-ppui": "true"
  }), jsx(CalendarContainer, {
    isCalendarVisible: isCalendarVisible,
    onShowCalendar: onShowCalendar,
    label: label,
    sheetContainerRef: sheetContainerRef,
    wrapperContainerRef: wrapperContainerRef,
    calendarTriggerRef: calendarTriggerRef,
    calendarInSheet: calendarInSheet,
    "data-ppui": "true"
  }, calendar));
  var unmaskedValue = _formatDate(selectedDate).replace(/\D/g, '');
  prevVal.current = unmaskedValue;
  return jsx(ClassNames, {
    "data-ppui": "true"
  }, function (_ref3) {
    var cx = _ref3.cx,
      css = _ref3.css;
    return jsx(MaskedTextInput, _extends({
      name: name,
      label: label,
      rightIcon: icon,
      maskOptions: _maskOptions,
      className: cx(css(_styles.wrapperContainer), className),
      inputMode: "numeric",
      "data-ppui-info": "date-input_3.8.8"
    }, rest, {
      onComplete: handleComplete,
      onAccept: handleAccept,
      value: unmaskedValue,
      ref: inputRef
    }));
  });
});
var CalendarRange;
(function (CalendarRange) {
  CalendarRange["START"] = "start";
  CalendarRange["END"] = "end";
})(CalendarRange || (CalendarRange = {}));
var MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
var DAY_NAMES = {
  sun: 'Sun',
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat'
};
function getFormattedDateFromParts(parts) {
  return parts.reduce(function (acc, _ref) {
    var type = _ref.type,
      value = _ref.value;
    if (type === 'month' || type === 'day' || type === 'year') {
      acc[type] = value;
    }
    return acc;
  }, {});
}
function defaultFormatToParts(date) {
  var parts = new Intl.DateTimeFormat('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric'
  }).formatToParts(date);
  return getFormattedDateFromParts(parts);
}
function isDateInRange(date, selectedDate, range, rangeCompletionDate) {
  if (range === CalendarRange.START && selectedDate && rangeCompletionDate) {
    return date.getTime() >= selectedDate.getTime() && date.getTime() <= rangeCompletionDate.getTime();
  } else if (range === CalendarRange.END && selectedDate && rangeCompletionDate) {
    return date.getTime() <= selectedDate.getTime() && date.getTime() >= rangeCompletionDate.getTime();
  }
  return false;
}
function isDateEqual(date, providedDate) {
  return providedDate && date.getTime() === providedDate.getTime();
}
function isDateRangeStart(date, selectedDate, range, rangeCompletionDate) {
  return range === CalendarRange.START && selectedDate && date.getTime() === selectedDate.getTime() || range === CalendarRange.END && rangeCompletionDate && date.getTime() === rangeCompletionDate.getTime();
}
function isDateRangeEnd(date, selectedDate, range, rangeCompletionDate) {
  return range === CalendarRange.END && selectedDate && date.getTime() === selectedDate.getTime() || range === CalendarRange.START && rangeCompletionDate && date.getTime() === rangeCompletionDate.getTime();
}
function getDatesForMonth(date, daysOfWeek, firstDayOfWeek, selectedDate, range, rangeCompletionDate, dueDate, isDateDisabled, calendarRange) {
  var dates = [];
  var startMonth = date.getMonth();
  var startYear = date.getFullYear();
  var from = new Date(startYear, startMonth, 1);
  var firstday = Object.keys(DAY_NAMES)[from.getDay()];
  var firstDayIndex = daysOfWeek.indexOf(firstday);
  var firstDOWIndex = Object.keys(DAY_NAMES).indexOf(firstDayOfWeek);
  var todayDate = new Date();
  var today = new Date(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate());
  from.setDate(-firstDayIndex + 1);
  while (from) {
    var month = from.getMonth();
    var day = from.getDate();
    var year = from.getFullYear();
    if (year === startYear && month > startMonth || year > startYear) {
      if (from.getDay() === firstDOWIndex) break;
    }
    var _dateObj = new Date(year, month, day);
    var isDisabled = isDateDisabled && isDateDisabled(_dateObj);
    if (calendarRange === CalendarRange.START && (month < startMonth || year < startYear)) {
      isDisabled = true;
    }
    if (calendarRange === CalendarRange.END && (month > startMonth || year > startYear)) {
      isDisabled = true;
    }
    dates.push({
      day: day,
      date: _dateObj,
      isDisabled: isDisabled,
      isToday: isDateEqual(from, today),
      inCurrentMonth: month === startMonth,
      isSelected: isDateEqual(from, selectedDate),
      isDue: isDateEqual(from, dueDate),
      inRange: isDateInRange(from, selectedDate, range, rangeCompletionDate),
      isRangeStart: isDateRangeStart(from, selectedDate, range, rangeCompletionDate),
      isRangeEnd: isDateRangeEnd(from, selectedDate, range, rangeCompletionDate)
    });
    from.setDate(day + 1);
  }
  return dates;
}
function getDaysOfWeek(firstDayOfWeek) {
  var daysOfWeek = Object.keys(DAY_NAMES);
  var firstDOWIndex = daysOfWeek.indexOf(firstDayOfWeek);
  if (firstDOWIndex === 0) {
    return daysOfWeek;
  }
  return daysOfWeek.slice(firstDOWIndex).concat(daysOfWeek.slice(0, firstDOWIndex));
}
function getFirstDayOfWeek(intlContext, firstDayOfWeek) {
  if (intlContext && intlContext.worldReadyObj && intlContext.worldReadyObj.weekInfo) {
    return firstDayOfWeek || intlContext.worldReadyObj.weekInfo.firstDay;
  }
  return firstDayOfWeek || 'sun';
}
function getNames(intlContext, monthNames, dayNames) {
  if (intlContext && intlContext.getDisplayNamesObject) {
    var monthDisplayNames = intlContext.getDisplayNamesObject({
      type: 'month',
      style: 'wide'
    });
    var _monthNames = monthDisplayNames.select(['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12']);
    var dayDisplayNames = intlContext.getDisplayNamesObject({
      type: 'day',
      style: 'abbreviated'
    });
    var keys = Object.keys(DAY_NAMES);
    var values = dayDisplayNames.select(keys);
    var _dayNames = keys.reduce(function (acc, key, index) {
      acc[key] = values[index];
      return acc;
    }, {});
    return {
      _monthNames: monthNames || _monthNames,
      _dayNames: dayNames || _dayNames
    };
  }
  return {
    _monthNames: monthNames || MONTH_NAMES,
    _dayNames: dayNames || DAY_NAMES
  };
}
function getFormatter(intlContext, formatToParts, formatterSkeleton) {
  if (intlContext && intlContext.dateTimeFormatter) {
    var dateTimeFormatter = intlContext.dateTimeFormatter({
      style: formatterSkeleton,
      // eslint-disable-next-line
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
    return formatToParts || function fn(date) {
      var parts = dateTimeFormatter.formatToParts(date);
      return getFormattedDateFromParts(parts);
    };
  }
  return formatToParts || defaultFormatToParts;
}
function areMonthsEqual(date1, date2) {
  return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth();
}
/* eslint-disable complexity */

var Calendar = function (_ref2) {
  var className = _ref2.className,
    monthNames = _ref2.monthNames,
    dayNames = _ref2.dayNames,
    firstDayOfWeek = _ref2.firstDayOfWeek,
    _ref2$prevMonthLabel = _ref2.prevMonthLabel,
    prevMonthLabel = _ref2$prevMonthLabel === void 0 ? 'Go to prev month' : _ref2$prevMonthLabel,
    _ref2$nextMonthLabel = _ref2.nextMonthLabel,
    nextMonthLabel = _ref2$nextMonthLabel === void 0 ? 'Go to next month' : _ref2$nextMonthLabel,
    selectedDate = _ref2.selectedDate,
    range = _ref2.range,
    rangeCompletionDate = _ref2.rangeCompletionDate,
    isFocusable = _ref2.isFocusable,
    dateAs = _ref2.dateAs,
    calendarFrom = _ref2.calendarFrom,
    calendarTo = _ref2.calendarTo,
    navClassName = _ref2.navClassName,
    tableClassName = _ref2.tableClassName,
    dateClassName = _ref2.dateClassName,
    dueDate = _ref2.dueDate,
    formatToParts = _ref2.formatToParts,
    _ref2$formatterSkelet = _ref2.formatterSkeleton,
    formatterSkeleton = _ref2$formatterSkelet === void 0 ? 'yyyyMd' : _ref2$formatterSkelet,
    _ref2$legendSelectedD = _ref2.legendSelectedDateLabel,
    legendSelectedDateLabel = _ref2$legendSelectedD === void 0 ? 'Selected' : _ref2$legendSelectedD,
    _ref2$legendDueDateLa = _ref2.legendDueDateLabel,
    legendDueDateLabel = _ref2$legendDueDateLa === void 0 ? 'Due date' : _ref2$legendDueDateLa,
    _ref2$legendTodayLabe = _ref2.legendTodayLabel,
    legendTodayLabel = _ref2$legendTodayLabe === void 0 ? 'Today' : _ref2$legendTodayLabe,
    isDateDisabled = _ref2.isDateDisabled,
    onSelection = _ref2.onSelection,
    onMonthChange = _ref2.onMonthChange;

  /* istanbul ignore next */
  if (!onSelection) {
    onSelection = function () {
      return devWarning('Required prop `onSelection` is missing');
    };
  }
  var tokens = useTheme();
  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  var _currentDate = selectedDate || new Date();
  var _useState = useState(new Date(_currentDate.getFullYear(), _currentDate.getMonth())),
    _useState2 = _slicedToArray(_useState, 2),
    currentDate = _useState2[0],
    setCurrentDate = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isRTL = _useState4[0],
    setRTL = _useState4[1];
  var intlContext = useContext(IntlContext);
  var containerRef = useCallback(function (node) {
    if (node !== null) {
      var computedStyle = window.getComputedStyle(node);
      setRTL(computedStyle && computedStyle.direction === 'rtl');
    }
  }, []);
  var _firstDayOfWeek = useMemo(function () {
    return getFirstDayOfWeek(intlContext, firstDayOfWeek);
  }, [firstDayOfWeek]);
  var _useMemo = useMemo(function () {
      return getNames(intlContext, monthNames, dayNames);
    }, [monthNames, dayNames]),
    _monthNames = _useMemo._monthNames,
    _dayNames = _useMemo._dayNames;
  var _formatToParts = useMemo(function () {
    return getFormatter(intlContext, formatToParts, formatterSkeleton);
  }, [formatToParts, formatterSkeleton]);
  var tabIndex = !isFocusable ? -1 : 0;
  useEffect(function () {
    setCurrentDate(new Date(_currentDate.getFullYear(), _currentDate.getMonth()));
  }, [selectedDate]);
  var renderDateButton = function (dateInfo) {
    var isDisabled = dateInfo.isDisabled,
      isToday = dateInfo.isToday,
      isSelected = dateInfo.isSelected,
      isRangeStart = dateInfo.isRangeStart,
      isRangeEnd = dateInfo.isRangeEnd,
      isDue = dateInfo.isDue,
      inCurrentMonth = dateInfo.inCurrentMonth,
      inRange = dateInfo.inRange,
      date = dateInfo.date;
    var shouldApplySelectedStyles = !isDisabled && (isSelected || isRangeStart || isRangeEnd);
    var cssVal = [_styles.calendarDate, !inCurrentMonth && _styles.calendarDateNotCurrent, isDisabled && _styles.calendarDateDisabled, isToday && _styles.calendarDateToday, isDue && _styles.calendarDueDate, inRange && _styles.calendarDateInRange, shouldApplySelectedStyles && _styles.calendarDateSelected, shouldApplySelectedStyles && isDue && _styles.calendarDueDateSelected];
    var props = {
      css: cssVal,
      className: dateClassName,
      onMouseDown: function onMouseDown(evt) {
        return evt.preventDefault();
      },
      onClick: function onClick() {
        return !(isDisabled || isSelected) && onSelection(date);
      },
      disabled: isDisabled,
      tabIndex: tabIndex,
      type: 'button'
    };
    if (dateAs) {
      props.dateInfo = dateInfo;
    }
    var formattedDate = _formatToParts(date);
    var button = jsx(dateAs || 'button', _extends({}, props, {
      "data-ppui": "true"
    }), formattedDate.day);
    if (isDue) {
      return jsx("div", {
        css: _styles.calendarDueDateIconContainer,
        "data-ppui": "true"
      }, button, jsx(ClockSmIcon, {
        size: "xs",
        css: _styles.calendarDueDateIcon,
        "data-ppui": "true"
      }));
    }
    return button;
  };
  var currentMonth = _monthNames[currentDate.getMonth()];
  var daysOfWeek = getDaysOfWeek(_firstDayOfWeek);
  var header = jsx("thead", {
    "data-ppui": "true"
  }, jsx("tr", {
    "data-ppui": "true"
  }, daysOfWeek.map(function (name) {
    return jsx("th", {
      key: name,
      "data-ppui": "true"
    }, jsx(CaptionText, {
      "data-ppui": "true"
    }, _dayNames[name]));
  })));
  var isPrevHidden = calendarFrom && areMonthsEqual(currentDate, calendarFrom);
  var isNextHidden = calendarTo && areMonthsEqual(currentDate, calendarTo);
  var calendarRange = '';
  if (isPrevHidden) {
    calendarRange = CalendarRange.START;
  }
  if (isNextHidden) {
    calendarRange = CalendarRange.END;
  }
  var dates = getDatesForMonth(currentDate, daysOfWeek, _firstDayOfWeek, selectedDate, range, rangeCompletionDate, dueDate, isDateDisabled, calendarRange);
  var dateRows = [];
  var shouldApplyRangeStyles = true;
  var isDueDateVisible = false;
  if (isDateDisabled) {
    var isSelectedDateDisabled = selectedDate && isDateDisabled(selectedDate);
    var isRangeCompletionDateDisabled = rangeCompletionDate && isDateDisabled(rangeCompletionDate);
    /* istanbul ignore next */

    if (isSelectedDateDisabled || isRangeCompletionDateDisabled) {
      shouldApplyRangeStyles = false;
    }
  }
  for (var i = 0; i < dates.length; i = i + 7) {
    var dateCols = [];
    for (var j = 0; j < 7; j++) {
      var _dates = dates[i + j],
        isRangeStart = _dates.isRangeStart,
        isRangeEnd = _dates.isRangeEnd,
        inRange = _dates.inRange,
        isDue = _dates.isDue;
      var allowInRange = inRange; // if start and end date is same don't need range styles
      // @ts-ignore

      if (isRangeStart && isRangeEnd) {
        allowInRange = false;
      }
      var cssVal = [shouldApplyRangeStyles && allowInRange && _styles.calendarCellInRange, shouldApplyRangeStyles && allowInRange && isRangeStart && _styles.calendarCellRangeStart, shouldApplyRangeStyles && allowInRange && isRangeEnd && _styles.calendarCellRangeEnd];
      if (!isDueDateVisible && isDue) {
        isDueDateVisible = true;
      }
      dateCols.push(jsx("td", {
        key: j,
        "data-ppui": "true"
      }, jsx("div", {
        css: cssVal,
        "data-ppui": "true"
      }, renderDateButton(dates[i + j]))));
    }
    dateRows.push(jsx("tr", {
      key: i,
      "data-ppui": "true"
    }, dateCols));
  }
  var body = jsx("tbody", {
    "data-ppui": "true"
  }, dateRows);
  var formattedCurrentDate = _formatToParts(currentDate);
  var legend = jsx("div", {
    css: _styles.legend,
    "data-ppui": "true"
  }, jsx("div", {
    css: _styles.legendItem,
    "data-ppui": "true"
  }, jsx("span", {
    css: [_styles.legendKey, _styles.calendarDateSelected],
    "data-ppui": "true"
  }), jsx(CaptionText, {
    as: "span",
    "data-ppui": "true"
  }, legendSelectedDateLabel)), isDueDateVisible && jsx("div", {
    css: _styles.legendItem,
    "data-ppui": "true"
  }, jsx(ClockSmIcon, {
    size: "xs",
    css: _styles.dueDateLegendKey,
    "data-ppui": "true"
  }), jsx(CaptionText, {
    as: "span",
    "data-ppui": "true"
  }, legendDueDateLabel)), jsx("div", {
    css: _styles.legendItem,
    "data-ppui": "true"
  }, jsx("span", {
    css: [_styles.legendKey, _styles.calendarDateToday],
    "data-ppui": "true"
  }), jsx(CaptionText, {
    as: "span",
    "data-ppui": "true"
  }, legendTodayLabel)));
  /* istanbul ignore next */

  return jsx("div", {
    className: className,
    ref: containerRef,
    "data-ppui": "true"
  }, jsx("div", {
    css: _styles.calendarNav,
    className: navClassName,
    "data-ppui": "true"
  }, jsx(IconButton, {
    svgIconComponent: isRTL ? ArrowRightIcon : ArrowLeftIcon,
    buttonType: "tertiary",
    onMouseDown: function onMouseDown(evt) {
      return evt.preventDefault();
    },
    onClick: function handlePrevClick() {
      currentDate.setMonth(currentDate.getMonth() - 1);
      var prevDate = new Date(currentDate.getFullYear(), currentDate.getMonth());
      setCurrentDate(prevDate);
      /* istanbul ignore next */

      if (onMonthChange) {
        onMonthChange(prevDate);
      }
    },
    css: [_styles.calendarNavButtonPrev, !isFocusable && _styles.calendarNavButtonsNotFocusable, isPrevHidden && _styles.calendarNavButtonsHidden],
    tabIndex: tabIndex,
    "data-ppui": "true"
  }, prevMonthLabel), jsx(BodyText, {
    "data-ppui": "true"
  }, "".concat(currentMonth, " ").concat(formattedCurrentDate.year)), jsx(IconButton, {
    svgIconComponent: isRTL ? ArrowLeftIcon : ArrowRightIcon,
    buttonType: "tertiary",
    onMouseDown: function onMouseDown(evt) {
      return evt.preventDefault();
    },
    onClick: function handleNextClick() {
      currentDate.setMonth(currentDate.getMonth() + 1);
      var nextDate = new Date(currentDate.getFullYear(), currentDate.getMonth());
      setCurrentDate(nextDate);
      /* istanbul ignore next */

      if (onMonthChange) {
        onMonthChange(nextDate);
      }
    },
    css: [_styles.calendarNavButtonNext, !isFocusable && _styles.calendarNavButtonsNotFocusable, isNextHidden && _styles.calendarNavButtonsHidden],
    tabIndex: tabIndex,
    "data-ppui": "true"
  }, nextMonthLabel)), jsx("table", {
    css: _styles.calendarTable,
    className: tableClassName,
    "data-ppui": "true"
  }, header, body), legend);
};
export { Calendar, DateInput, styles as dateInputStyles };